import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Zap, Shield, Users, ArrowRight } from 'lucide-react';
import Header from './Header';
import InlandFiberLogo from './InlandFiberLogo';
import './Landing.css';

const Landing = () => {
  return (
    <div>
      <Header />
      <main>
        {/* Hero Section */}
        <section className="hero">
          <div className="hero-bg" aria-hidden="true" />
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-orb hero-orb-1" aria-hidden="true" />
          <div className="hero-orb hero-orb-2" aria-hidden="true" />
          <div className="hero-orb hero-orb-3" aria-hidden="true" />
          <div className="hero-content">
            <div className="hero-inner animate-fade-in">
              <p className="hero-eyebrow">Your Data Center Portal</p>
              <h1 className="hero-title">
                One Portal.
                <span className="hero-title-accent">Everything in One Place.</span>
              </h1>
              <p className="hero-subtitle">
                Indy AI for intelligent chat, Colo by the U to size and price colocation—plus access and manage your services. Your single hub for data center services.
              </p>
              <div className="hero-actions">
                <Link to="/register" className="btn btn-primary hero-btn-primary">
                  Get Started
                  <ArrowRight className="hero-btn-icon" aria-hidden="true" />
                </Link>
                <Link to="/pricing" className="btn btn-outline hero-btn-secondary">
                  View Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%)'}}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{color: '#f9fafb'}}>
                Our Services
              </h2>
              <p className="text-xl max-w-2xl mx-auto" style={{color: '#d1d5db'}}>
                AI, colocation, and your services—all in one place for your data center needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Indy AI Service */}
              <div className="text-center card p-8 card-hover animate-slide-up">
                <div className="feature-icon">
                  <MessageSquare className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-semibold mb-4" style={{color: '#f9fafb'}}>
                  Indy AI
                </h3>
                <p className="leading-relaxed mb-4" style={{color: '#d1d5db'}}>
                  An intelligent chat assistant designed to help with questions, tasks, and content creation. 
                  Get instant, intelligent responses powered by advanced AI models.
                </p>
                <ul className="text-left space-y-2" style={{color: '#d1d5db'}}>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Intelligent chat assistant</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Help with questions and tasks</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Content creation support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Affordable monthly subscription</span>
                  </li>
                </ul>
              </div>

              {/* Colo by the U Service */}
              <div className="text-center card p-8 card-hover animate-slide-up" style={{animationDelay: '0.1s'}}>
                <div className="feature-icon">
                  <Shield className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-semibold mb-4" style={{color: '#f9fafb'}}>
                  Colo by the U
                </h3>
                <p className="leading-relaxed mb-4" style={{color: '#d1d5db'}}>
                  Calculate your colocation needs with our intuitive calculator. 
                  Find the right server solution and get transparent pricing for your business.
                </p>
                <ul className="text-left space-y-2" style={{color: '#d1d5db'}}>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Colocation calculator</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Transparent pricing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Size and price your setup</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-2" style={{color: '#10b981'}}>✓</span>
                    <span>Single server and beyond</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Additional Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
              <div className="text-center card card-small card-hover animate-slide-up" style={{animationDelay: '0.2s', padding: '2rem 1.5rem'}}>
                <div className="feature-icon feature-icon-small">
                  <Zap className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{color: '#f9fafb'}}>
                  Lightning Fast
                </h3>
                <p className="text-sm leading-relaxed" style={{color: '#d1d5db'}}>
                  Optimized for speed and performance
                </p>
              </div>

              <div className="text-center card card-small card-hover animate-slide-up" style={{animationDelay: '0.3s', padding: '2rem 1.5rem'}}>
                <div className="feature-icon feature-icon-small">
                  <Shield className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{color: '#f9fafb'}}>
                  Secure & Reliable
                </h3>
                <p className="text-sm leading-relaxed" style={{color: '#d1d5db'}}>
                  Enterprise-grade security and reliability
                </p>
              </div>

              <div className="text-center card card-small card-hover animate-slide-up" style={{animationDelay: '0.4s', padding: '2rem 1.5rem'}}>
                <div className="feature-icon feature-icon-small">
                  <Users className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{color: '#f9fafb'}}>
                  Expert Support
                </h3>
                <p className="text-sm leading-relaxed" style={{color: '#d1d5db'}}>
                  Priority support when you need it
                </p>
              </div>

              <div className="text-center card card-small card-hover animate-slide-up" style={{animationDelay: '0.5s', padding: '2rem 1.5rem'}}>
                <div className="feature-icon feature-icon-small">
                  <MessageSquare className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{color: '#f9fafb'}}>
                  Easy Integration
                </h3>
                <p className="text-sm leading-relaxed" style={{color: '#d1d5db'}}>
                  Simple setup and seamless integration
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20" style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)'}}>
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{color: '#f9fafb'}}>
              Ready to Get Started?
            </h2>
            <p className="text-xl mb-8" style={{color: '#d1d5db'}}>
              Join users who are already leveraging Inland Fiber Services to boost their productivity and streamline their operations.
            </p>
            <Link to="/register" className="btn btn-primary" style={{fontSize: '1.1rem', padding: '1rem 2.5rem', minWidth: '220px'}}>
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)', color: 'white'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <InlandFiberLogo
                alt="Inland Fiber Services Logo"
                className="h-12 w-12 mr-3"
                style={{ display: 'block', height: '3rem', width: 'auto', maxWidth: '120px' }}
              />
              <h3 className="text-2xl font-bold" style={{color: '#f8fafc'}}>Inland Fiber Services</h3>
            </div>
            <p className="mb-4" style={{color: '#cbd5e1'}}>
              Your all-in-one data center portal—AI, colo, and your services
            </p>
            <div className="flex justify-center space-x-6">
              <Link to="/pricing" className="hover:text-white transition-colors" style={{color: '#cbd5e1'}}>
                Services
              </Link>
              <Link to="/colo-calculator" className="hover:text-white transition-colors" style={{color: '#cbd5e1'}}>
                Colo by the U
              </Link>
              <Link to="/login" className="hover:text-white transition-colors" style={{color: '#cbd5e1'}}>
                Login
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;